/**
 * 환경 변수 설정
 * React에서는 REACT_APP_ 접두사가 필요합니다.
 */
import { logger } from '../utils/logger'

const getEnvVar = (
  name: string,
  options: {
    required?: boolean
    fallback?: string
  } = {}
): string => {
  const value = process.env[name]

  if (value && value.trim().length > 0) {
    return value.trim()
  }

  if (options.required) {
    logger.error(`❌ Missing required environment variable: ${name}`)
  } else {
    if (options.fallback !== undefined) {
      logger.info(
        `ℹ️ Environment variable not set (${name}), using fallback value`
      )
    }
  }

  return options.fallback ?? ''
}

const nodeEnv = process.env.NODE_ENV || 'development'
const isDevelopment = nodeEnv === 'development'
const isProduction = nodeEnv === 'production'
const disableSocialAndSignupInProd =
  isProduction && process.env.REACT_APP_DISABLE_SOCIAL_AND_SIGNUP_IN_PROD === 'true'

// 개발 환경 인증 스킵(디폴트: false)
// - true: RouteGuard가 인증 검증을 건너뜀(로컬 개발 편의)
// - false: 개발 환경에서도 운영과 동일하게 인증 검증 수행(권장)
const skipAuthInDev = process.env.REACT_APP_SKIP_AUTH_IN_DEV === 'true'

// API base URL
// - 프록시를 쓰지 않는 로컬 개발에서는 백엔드(8081)를 직접 바라보도록 절대 URL 사용을 권장한다.
// - 예: REACT_APP_API_BASE_URL=http://localhost:8081/api
/** 랜딩 전용 — 사전 등록은 AWS API GW. Core API URL 은 미사용 시 비워도 됨 */
const apiBaseUrl = getEnvVar('REACT_APP_API_BASE_URL', {
  fallback: isDevelopment ? 'http://localhost:8081/api' : '',
})

/** Core API 호스트(예: https://api.example.com) — `/api` 접두 제거. `/static/img` 등 정적 경로 붙일 때 사용 */
const apiOrigin = apiBaseUrl.replace(/\/api\/?$/, '').replace(/\/$/, '')

const fourCutBaseUrl = getEnvVar('REACT_APP_4CUT_API_BASE_URL', {
  required: isProduction
})

const kakaoRedirectUri = isDevelopment
  ? getEnvVar('REACT_APP_KAKAO_REDIRECT_URI', {
      fallback: 'http://localhost:3000/login/callback/kakao'
    })
  : getEnvVar('REACT_APP_KAKAO_REDIRECT_URI', { required: true })

/** 로컬·스테이징 등에서 `/test` 등 디버그 라우트를 켜려면 `REACT_APP_ENABLE_DEV_ROUTES=true` */
const enableDevRoutes =
  isDevelopment || process.env.REACT_APP_ENABLE_DEV_ROUTES === 'true'

export const config = {
  // API 설정
  api: {
    baseUrl: apiBaseUrl,
    /** API 베이스에서 `/api` 제거한 오리진 (이미지 `/static/img/...` 절대화용) */
    origin: apiOrigin,
    timeout: 30000 // 30초
  },

  // 4컷 스토리 생성 API 설정
  fourCutApi: {
    baseUrl: fourCutBaseUrl,
    useRealApi:
      process.env.REACT_APP_USE_REAL_4CUT_API === 'true' || isProduction
  },

  // 환경 설정
  env: {
    nodeEnv: process.env.REACT_APP_NODE_ENV || nodeEnv,
    isDevelopment,
    isProduction,
    disableSocialAndSignupInProd,
    enableDevRoutes
  },

  // 인증 설정
  auth: {
    skipInDev: skipAuthInDev
  },

  // 카카오 로그인 설정
  kakao: {
    clientId: getEnvVar('REACT_APP_KAKAO_CLIENT_ID'),
    redirectUri: kakaoRedirectUri
  },

  // 네이버 로그인 (클라이언트 ID가 있을 때만 활성)
  naver: {
    clientId: getEnvVar('REACT_APP_NAVER_CLIENT_ID')
  },
  recaptcha: {
    siteKey: getEnvVar('REACT_APP_RECAPTCHA_SITE_KEY', {
      fallback: '6Lez6tEsAAAAABer9ITUjXpjNhBOB3HH9R7DxLTF',
    }),
    action: getEnvVar('REACT_APP_RECAPTCHA_ACTION', { fallback: 'contact_submit' }),
    verifyEnabled: getEnvVar('REACT_APP_RECAPTCHA_VERIFY_ENABLED', {
      fallback: 'true',
    }) === 'true',
  },

  /** 문의하기 폼 — API source 필드 */
  contact: {
    source: getEnvVar('REACT_APP_CONTACT_SOURCE', { fallback: 'storytailor' }),
  },

  /**
   * Firebase 구글·이메일 로그인 UI 동작 여부.
   * false(미설정)이면 버튼은 보이되 클릭해도 동작하지 않음.
   */
  enableFirebaseLogin: process.env.REACT_APP_ENABLE_FIREBASE_LOGIN === 'true',
  firebase: {
    apiKey: getEnvVar('REACT_APP_FIREBASE_API_KEY'),
    authDomain: getEnvVar('REACT_APP_FIREBASE_AUTH_DOMAIN'),
    projectId: getEnvVar('REACT_APP_FIREBASE_PROJECT_ID'),
    storageBucket: getEnvVar('REACT_APP_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: getEnvVar('REACT_APP_FIREBASE_MESSAGING_SENDER_ID'),
    appId: getEnvVar('REACT_APP_FIREBASE_APP_ID'),
    measurementId: getEnvVar('REACT_APP_FIREBASE_MEASUREMENT_ID'),
    emailVerificationContinueUrl: getEnvVar(
      'REACT_APP_FIREBASE_EMAIL_VERIFY_CONTINUE_URL'
    )
  },

  // Google Analytics 4 (gtag)
  analytics: {
    measurementId: getEnvVar('REACT_APP_GA_MEASUREMENT_ID'),
  },

  /** 네이버 광고 전환추적 (wcs) — AccountId 미설정 시 스크립트 미로드 */
  naverWcs: {
    accountId: getEnvVar('REACT_APP_NAVER_WCS_ACCOUNT_ID'),
    inflowDomain: getEnvVar('REACT_APP_NAVER_WCS_INFLOW_DOMAIN', {
      fallback: 'storytailor.co.kr',
    }),
  },

  /**
   * Enliple OneTag(모비온) 비쇼핑 피드 + 데이터매니저 페이지 이동(callDM)
   * - 스크립트: public/index.html
   * - 이벤트 코드: src/config/oneTagEvents.ts
   * - REACT_APP_ONETAG_ENABLED=false 로 CallMtm·callDM 비활성
   */
  oneTag: {
    enabled: process.env.REACT_APP_ONETAG_ENABLED !== 'false',
    eid: getEnvVar('REACT_APP_ONETAG_EID', {
      fallback: 'tofp4xqc1c9xtofp4xqc1c',
    }),
  },

  // 앱 설정
  app: {
    name: getEnvVar('REACT_APP_APP_NAME', { fallback: 'StoryTailor' }),
    version: getEnvVar('REACT_APP_VERSION', { fallback: '1.0.0' })
  },

  // 로깅 설정 (실제 출력은 utils/logger · consoleSuppression 과 동일한 env 기준)
  logging: {
    enabled:
      process.env.REACT_APP_ENABLE_CONSOLE_LOGS === 'true' ||
      process.env.REACT_APP_DEBUG === 'true' ||
      isDevelopment,
    level: getEnvVar('REACT_APP_LOG_LEVEL', {
      fallback: isDevelopment ? 'debug' : 'error'
    }),
    showTimestamp: getEnvVar('REACT_APP_LOG_TIMESTAMP', {
      fallback: 'true'
    }) !== 'false',
    showLevel:
      getEnvVar('REACT_APP_LOG_SHOW_LEVEL', { fallback: 'true' }) !== 'false'
  },

  // 파일 업로드 설정
  upload: {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
    compression: {
      maxWidth: 1920,
      maxHeight: 1080,
      quality: 0.8
    }
  }
}

// 환경 변수 검증
export const validateEnvironment = () => {
  const requiredVars: string[] = []

  const optionalVars = [
    'REACT_APP_API_BASE_URL',
    'REACT_APP_4CUT_API_BASE_URL',
    'REACT_APP_KAKAO_CLIENT_ID',
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID',
    'REACT_APP_FIREBASE_MEASUREMENT_ID',
    'REACT_APP_GA_MEASUREMENT_ID',
    'REACT_APP_NAVER_WCS_ACCOUNT_ID',
    'REACT_APP_NAVER_WCS_INFLOW_DOMAIN',
    'REACT_APP_ONETAG_ENABLED',
    'REACT_APP_ONETAG_EID',
    'REACT_APP_RECAPTCHA_SITE_KEY',
    'REACT_APP_RECAPTCHA_ACTION',
    'REACT_APP_RECAPTCHA_VERIFY_ENABLED',
    'REACT_APP_FIREBASE_EMAIL_VERIFY_CONTINUE_URL',
    'REACT_APP_DEBUG',
    'REACT_APP_ENABLE_CONSOLE_LOGS',
    'REACT_APP_LOG_LEVEL',
    'REACT_APP_LOG_TIMESTAMP',
    'REACT_APP_LOG_SHOW_LEVEL'
  ]

  const missingVars = requiredVars.filter(varName => !process.env[varName])
  const missingOptionalVars = optionalVars.filter(
    varName => !process.env[varName]
  )

  if (missingVars.length > 0) {
    logger.error('❌ Missing required environment variables:', missingVars)
    logger.error('Please create a .env file with the required variables')
    return false
  }

  if (missingOptionalVars.length > 0) {
    logger.warn('⚠️ Missing optional environment variables:', missingOptionalVars)
  }

  logger.info('✅ Environment variables validated successfully')
  logger.debug('🔧 Current API Base URL:', config.api.baseUrl)
  logger.debug('🔧 NODE_ENV:', nodeEnv)
  return true
}

// 개발 환경에서만 환경 변수 검증 실행
if (config.env.isDevelopment) {
  validateEnvironment()
}
